import type { FunctionComponent } from 'react';
import type { DehydratedState } from '@tanstack/react-query';
import type { i18n } from 'i18next';

import type { Country, DeviceType, OddsFormat, Repub, StateCode, SubeventType } from '@cx-modules/types';

import type { CategoryGroupId } from './module';

export type OddsGridType = FunctionComponent<OddsGridIndexProps>;
export type OddsGridIndexProps = OddsGridProps & {
  dehydratedState: DehydratedState;
  deviceType: DeviceType;
};
export type OddsGridSandboxProps = Omit<OddsGridIndexProps, 'dehydratedState'> & {
  hideControls?: boolean;
};
export const oddsShownType = {
  BOOKMAKER_CLICKABLE: 'BOOKMAKER_CLICKABLE',
  OC_NON_CLICKABLE: 'OC_NON_CLICKABLE',
} as const;
export type OddsShown = (typeof oddsShownType)[keyof typeof oddsShownType];
/**
 * A bookmaker ordering override. When `overrideBookies` is supplied, listed bookmakers are moved
 * to the front of the grid in ascending `priority` order (lowest first); unlisted bookmakers
 * follow in their existing API-priority order. Only the `code` is sent to the backend — the
 * `priority` is a client-side display concern.
 */
export type OverrideBookie = { code: string; priority: number };
export type OnOddsButtonClick = (
  action: string,
  betId: string | number,
  bookieCode: string,
  betName?: string,
  oddsText?: string | number,
) => void;
export type OddsGridProps = Omit<LoadOddsGridArgs, 'dataApiEndpoint'> & {
  autoContrastOfferTooltips?: boolean;
  bestOddsStartExpanded: boolean;
  betIdsToShow?: number[];
  categoryGroupId: CategoryGroupId;
  centrifugoUrl?: string;
  dataApiEndpoint?: string;
  disableStreamer?: boolean;
  /**
   * When `true`, opens a Centrifugo websocket connection and subscribes to a
   * channel per bookmaker (`market:{marketId}:bookmaker:{bookieCode}`) to update
   * prices live. When `false` (the default) no connection is opened and prices
   * remain as fetched from `/grid`.
   *
   * @default false
   */
  enableLivePrices?: boolean;
  enforceMobile?: boolean;
  firstBetOnMobileStartsExpanded?: boolean;
  /**
   * When `true`, hides the best-odds panel (MarketExpander accordion and
   * "Compare All Odds" button) and renders only the grid/table directly.
   * The market header is also suppressed on desktop.
   *
   * The `minUniqueBookies` / no-data fallback still applies — an empty grid
   * is never forced.
   *
   * @default false
   */
  gridOnly?: boolean;
  gridStartsExpanded?: boolean;
  hideHistory?: boolean;
  i18n: i18n;
  marketIndex: number;
  marketName: string;
  minUniqueBookies?: number;
  noDataAvailableFallbackText?: string;
  oddsFormat: OddsFormat;
  oddsShown: OddsShown;
  onOddsButtonClick?: OnOddsButtonClick;
  onOfferClick?: (action: string, bookieCode: string, clickEvent: Event, clickoutUrl: string) => void;
  overrideBookies?: OverrideBookie[];
  resourcesUrl: string;
  showBookieLogo?: boolean;
  showCompareOddsToolTip?: boolean;
  showMoreBetsStartsExpanded: boolean;
  showSubeventData?: boolean;
  subeventType: SubeventType;
  tooltipForNewUser?: boolean;
};
export type OddsGridStoreProps = Pick<
  OddsGridProps,
  | 'categoryGroupId'
  | 'disableStreamer'
  | 'firstBetOnMobileStartsExpanded'
  | 'tooltipForNewUser'
  | 'repub'
  | 'resourcesUrl'
  | 'oddsFormat'
  | 'oddsShown'
  | 'betIdsToShow'
  | 'marketName'
  | 'onOddsButtonClick'
  | 'onOfferClick'
  | 'autoContrastOfferTooltips'
  | 'showBookieLogo'
>;
export type LoadOddsGridArgs = {
  apiKey?: string;
  countryCode: Country | '';
  dataApiEndpoint: string;
  marketId: number;
  nocache?: boolean;
  overrideBookies?: OverrideBookie[];
  repub: Repub;
  subdivision: StateCode | '';
};
